// ============================================================================
// AdminDashboard.jsx (FULL WORKING VERSION WITH SIMPLE MODAL)
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import './AdminDashboard.css';

const PAGE_SIZE = 25;
const SESSION_TIMEOUT_MS = 1000 * 60 * 60 * 8; // 8 hours

// Add meta tags manually
function setAdminMeta() {
  document.title = 'GrammAA — Admin Panel';

  let m = document.querySelector('meta[name="robots"]');
  if (!m) {
    m = document.createElement('meta');
    m.name = 'robots';
    document.head.appendChild(m);
  }
  m.content = 'noindex, nofollow';
}

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [checks, setChecks] = useState([]);
  const [payments, setPayments] = useState([]);

  const [query, setQuery] = useState('');
  const [filterPremium, setFilterPremium] = useState('all');
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const [loading, setLoading] = useState(false);

  const adminToken = useRef(localStorage.getItem('admin_token'));
  const lastActivity = useRef(Date.now());

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);

  // =============================================================
  // INITIAL LOAD + SESSION WATCH
  // =============================================================
  useEffect(() => {
    setAdminMeta();

    if (!adminToken.current) {
      window.location.href = '/starmoonlight/login';
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken.current}`;

    loadAll();

    const t = setInterval(() => {
      if (Date.now() - lastActivity.current > SESSION_TIMEOUT_MS) {
        logoutAdmin('Session timed out');
      }
    }, 60000);

    const refresh = () => (lastActivity.current = Date.now());
    window.addEventListener('mousemove', refresh);
    window.addEventListener('keydown', refresh);

    return () => {
      clearInterval(t);
      window.removeEventListener('mousemove', refresh);
      window.removeEventListener('keydown', refresh);
    };
  }, []);

  function markActivity() {
    lastActivity.current = Date.now();
  }

  async function loadAll() {
    setLoading(true);
    try {
      await Promise.all([loadStats(), loadUsers(), loadChecks(), loadPayments()]);
    } catch (err) {
      toast.error('Failed to load admin data.');
      localStorage.removeItem('admin_token');
      window.location.href = '/starmoonlight/login';
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    const res = await axios.get(`${API}/admin/stats`);
    setStats(res.data);
  }

  async function loadPayments() {
    try {
      const res = await axios.get(`${API}/admin/payments`);
      setPayments(res.data || []);
    } catch {
      setPayments([]);
    }
  }

  async function loadChecks() {
    const res = await axios.get(`${API}/admin/checks`);
    setChecks(res.data || []);
  }

  async function loadUsers(pageToLoad = page, pageSize = PAGE_SIZE) {
    const params = {
      query: query || undefined,
      premium: filterPremium === 'all' ? undefined : filterPremium === 'premium',
      page: pageToLoad,
      page_size: pageSize
    };

    const res = await axios.get(`${API}/admin/users-list`, { params });

    if (res.data.users) {
      setUsers(res.data.users);
      setTotalUsers(res.data.total);
    } else {
      setUsers(res.data);
      setTotalUsers(res.data.length);
    }
  }

  // =============================================================
  // USER ACTIONS
  // =============================================================
  async function togglePremium(userId, isPremium) {
    markActivity();
    try {
     if (!isPremium) {
  // Give premium (default monthly)
  await axios.put(`${API}/admin/users/${userId}/premium-with-type?premium=true&plan=monthly`);
} else {
  // Remove premium
  await axios.put(`${API}/admin/users/${userId}/premium-with-type?premium=false`);
}
      toast.success('Premium status updated');
      loadUsers();
    } catch {
      toast.error('Failed to update');
    }
  }

  async function deleteUser(userId) {
    if (!window.confirm('Delete user permanently?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      loadUsers();
    } catch {
      toast.error('Failed to delete user');
    }
  }

  async function banUser(userId, banned = true) {
    try {
      await axios.put(`${API}/admin/users/${userId}/ban?banned=${banned}`);
      toast.success(banned ? 'User banned' : 'User unbanned');
      loadUsers();
    } catch {
      toast.error('Failed to update');
    }
  }

  async function resetPassword(userId) {
    if (!window.confirm('Reset password?')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-password`);
      toast.success('Password reset email sent');
    } catch {
      toast.error('Failed');
    }
  }

  function viewUser(user) {
    setSelectedUser(user);
    setShowUserModal(true);
  }

  function viewCheck(check) {
    setSelectedCheck(check);
    setShowCheckModal(true);
  }

  // =============================================================
  // ADD ADMIN
  // =============================================================
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');

  async function addAdminAccount(e) {
    e.preventDefault();
    if (!newAdminEmail || !newAdminPassword) return toast.error('Enter details');

    try {
      await axios.post(`${API}/admin/create`, {
        email: newAdminEmail,
        password: newAdminPassword
      });
      toast.success('Admin created');
      setShowAddAdminModal(false);
      setNewAdminEmail('');
      setNewAdminPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  }

  // =============================================================
  // EXPORT FUNCTIONS
  // =============================================================
  function exportCSV(filename, rows) {
    if (!rows.length) return toast('No data.');

    const keys = Object.keys(rows[0]);
    const csv = [
      keys.join(','),
      ...rows.map(r => keys.map(k => `"${String(r[k] ?? '')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
  }

  async function exportUsersCSV() {
    exportCSV('users.csv', users);
  }

  async function exportChecksCSV() {
    exportCSV('checks.csv', checks);
  }

  // =============================================================
  // LOGOUT
  // =============================================================
  function logoutAdmin(reason) {
    localStorage.removeItem('admin_token');
    toast.success(reason || 'Logged out');
    window.location.href = '/starmoonlight/login';
  }

  // =============================================================
  // RENDER
  // =============================================================
  return (
    <div className="admin-container">
      <div className="glass-background"></div>

      <nav className="admin-nav fade-in">
        <div className="nav-brand">GrammAA Admin</div>
        <div className="nav-links">
          <Button variant="ghost" onClick={exportUsersCSV}>Export Users</Button>
          <Button variant="ghost" onClick={exportChecksCSV}>Export Checks</Button>
          <Button variant="ghost" onClick={() => logoutAdmin('Logout')}>Logout</Button>
        </div>
      </nav>

      <div className="admin-content fade-in">
        <div className="admin-header">
          <h1>Admin Panel</h1>
          <p className="admin-subtitle">Manage users, logs, payments & settings</p>
        </div>

        {/* STATS */}
        {stats && (
          <div className="stats-grid">
            <Card className="stat-card glass-panel">
              <h3>Total Users</h3>
              <div className="stat-value">{stats.total_users}</div>
            </Card>
            <Card className="stat-card glass-panel">
              <h3>Premium Users</h3>
              <div className="stat-value">{stats.premium_users}</div>
            </Card>
            <Card className="stat-card glass-panel">
              <h3>Total Checks</h3>
              <div className="stat-value">{stats.total_checks}</div>
            </Card>
            <Card className="stat-card glass-panel">
              <h3>Total Payments</h3>
              <div className="stat-value">{stats.total_payments}</div>
            </Card>
          </div>
        )}

        <Tabs defaultValue="users" className="admin-tabs">
          <TabsList className="tabs-list">
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="checks">Grammar Logs</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
          </TabsList>

          {/* USERS TAB */}
          <TabsContent value="users">
            <Card className="admin-card glass-panel">

              <div className="users-top">
                <Input
                  placeholder="Search email..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />

                <select value={filterPremium} onChange={(e) => setFilterPremium(e.target.value)}>
                  <option value="all">All</option>
                  <option value="premium">Premium</option>
                  <option value="free">Free</option>
                </select>

                <Button onClick={() => setShowAddAdminModal(true)}>Add Admin</Button>
              </div>

              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Daily Limit</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td>{u.email}</td>
                        <td><Badge className={u.is_premium ? 'premium-badge' : 'free-badge'}>
                          {u.is_premium ? 'Premium' : 'Free'}
                        </Badge></td>
                        <td>{u.daily_limit}</td>
                        <td>{new Date(u.created_at).toLocaleString()}</td>
                        <td>
                          <Button size="sm" onClick={() => viewUser(u)}>View</Button>
                          <Button size="sm" variant="outline" onClick={() => togglePremium(u.id, u.is_premium)}>
                            {u.is_premium ? 'Remove Premium' : 'Make Premium'}
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => deleteUser(u.id)}>Delete</Button>
                          <Button size="sm" onClick={() => banUser(u.id, !u.banned)}>
                            {u.banned ? 'Unban' : 'Ban'}
                          </Button>
                          <Button size="sm" onClick={() => resetPassword(u.id)}>Reset PW</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </Card>
          </TabsContent>

          {/* CHECKS TAB */}
          <TabsContent value="checks">
            <Card className="admin-card glass-panel">
              <h2>Grammar Logs</h2>

              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Preview</th>
                      <th>Score</th>
                      <th>Mistakes</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {checks.map(c => (
                      <tr key={c.id}>
                        <td>{c.user_id?.substring(0, 8)}...</td>
                        <td>{c.original_text?.substring(0, 70)}...</td>
                        <td><Badge>{c.grammar_score}/100</Badge></td>
                        <td>{(c.mistakes || []).length}</td>
                        <td>{new Date(c.created_at).toLocaleString()}</td>
                        <td>
                          <Button size="sm" onClick={() => viewCheck(c)}>View</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </Card>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <Card className="admin-card glass-panel">
              <h2>System Settings</h2>
              <SettingsSection stats={stats} markActivity={markActivity} />
            </Card>
          </TabsContent>

          {/* PAYMENTS TAB */}
          <TabsContent value="payments">
            <Card className="admin-card glass-panel">
              <h2>Payments</h2>

              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Session</th>
                      <th>User</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {payments.map(p => (
                      <tr key={p.id}>
                        <td>{p.session_id}</td>
                        <td>{p.user_id?.substring(0, 8)}...</td>
                        <td>{p.amount} {p.currency}</td>
                        <td>{p.status}</td>
                        <td>{new Date(p.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </Card>
          </TabsContent>
        </Tabs>

        {/* ------------------------- USER MODAL ------------------------- */}
        {showUserModal && selectedUser && (
          <SimpleModal onClose={() => setShowUserModal(false)} title={`User: ${selectedUser.email}`}>
            <p><strong>ID:</strong> {selectedUser.id}</p>
            <p><strong>Email:</strong> {selectedUser.email}</p>
            <p><strong>Premium:</strong> {selectedUser.is_premium ? 'Yes' : 'No'}</p>
            <p><strong>Joined:</strong> {new Date(selectedUser.created_at).toLocaleString()}</p>

            <pre>{JSON.stringify(selectedUser, null, 2)}</pre>
          </SimpleModal>
        )}

        {/* ------------------------- CHECK MODAL ------------------------- */}
        {showCheckModal && selectedCheck && (
          <SimpleModal onClose={() => setShowCheckModal(false)} title={`Check ID: ${selectedCheck.id}`}>
            <h3>Original</h3>
            <p>{selectedCheck.original_text}</p>

            <h3>Corrected</h3>
            <p>{selectedCheck.corrected_text}</p>

            <h3>Mistakes</h3>
            <pre>{JSON.stringify(selectedCheck.mistakes, null, 2)}</pre>

            <h3>Rewrites</h3>
            <pre>{JSON.stringify(selectedCheck.rewrites, null, 2)}</pre>
          </SimpleModal>
        )}

        {/* ------------------------- ADD ADMIN MODAL ------------------------- */}
        {showAddAdminModal && (
          <SimpleModal onClose={() => setShowAddAdminModal(false)} title="Create Admin">
            <form onSubmit={addAdminAccount}>
              <Label>Email</Label>
              <Input value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} />

              <Label>Password</Label>
              <Input type="password" value={newAdminPassword} onChange={(e) => setNewAdminPassword(e.target.value)} />

              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <Button type="submit">Create</Button>
                <Button variant="outline" onClick={() => setShowAddAdminModal(false)}>Cancel</Button>
              </div>
            </form>
          </SimpleModal>
        )}

      </div>
    </div>
  );
};

// ============================================================================
// SETTINGS SUBCOMPONENT
// ============================================================================
const SettingsSection = ({ stats, markActivity }) => {
  const [dailyLimit, setDailyLimit] = useState(3);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (stats) setDailyLimit(stats.default_daily_limit || 3);
  }, [stats]);

  async function updateDailyLimit() {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings/daily-limit?new_limit=${dailyLimit}`);
      toast.success('Daily limit updated');
    } catch {
      toast.error('Failed');
    } finally {
      setLoading(false);
      markActivity();
    }
  }

  return (
    <div>
      <Label>Daily Limit (Free Users)</Label>
      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          type="number"
          min={1}
          max={999}
          value={dailyLimit}
          onChange={(e) => setDailyLimit(parseInt(e.target.value || '1'))}
        />
        <Button disabled={loading} onClick={updateDailyLimit}>
          {loading ? 'Updating...' : 'Update'}
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// SIMPLE MODAL (NO DEPENDENCIES)
// ============================================================================
const SimpleModal = ({ title, children, onClose }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 99999
    }}>
      <div style={{
        width: '500px',
        background: '#111827',
        padding: '20px',
        borderRadius: '10px',
        color: 'white'
      }}>
        <h2>{title}</h2>
        <div>{children}</div>
        <div style={{ marginTop: 20, textAlign: 'right' }}>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
